import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  buildHighlightMessage,
  type HighlightMessage,
} from "./buildHighlightMessage";
import { BASE_API_URL } from "../../constants";
import { getSessionToken } from "@/services/sessionToken";
import { FileViewerType } from "../FileViewer/services/fileViewerInterface";
import useCitationLinkInterception from "@/hooks/useCitationLinkInterception";
import type { CitationDeepLink } from "@/services/citationDeepLink";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { ResponseWrapper } from "../FileViewer/services/useFileLoader";
import { listenForViewerIframeEscape } from "../FileViewer/documentViewerEscape";
import { useFrameWidth } from "@/hooks/useFrameWidth";
import { findShortcutOf, listenForViewerFindShortcut } from "./findShortcut";
import {
  OutlineColumn,
  READING_AIDS_COMPACT_BELOW_PX,
  ReadingAids,
  type FindState,
  type ReadingAidsHandle,
  type OutlineEntry,
  type ViewerCommand,
  type ViewerState,
} from "./ReadingAids";

// The frame's answers to the reading aids (#12481): its page, page count
// and scale on every change; the document's outline once loaded; the find
// controller's count as it searches.
type StateMessage = { state: ViewerState };
type OutlineMessage = { outline: OutlineEntry[] };
type FindMessage = { find: FindState };
type CommandMessage = { command: ViewerCommand };
// The frame's Ctrl/Cmd-S (or any pdf.js save path) asking the host to run
// its Download, since the frame's own bytes are only the preview.
type DownloadRequestMessage = { requestDownload: true };

type Msg = { IS_PDF_VIEWER_MESSAGE: "YES" } & (
  | HighlightMessage
  | StateMessage
  | OutlineMessage
  | FindMessage
  | DownloadRequestMessage
);

interface PdfReaderProps {
  fileContentResponse?: ResponseWrapper;
  fileUrl?: string;
  /** Overrides how a citation clicked inside the PDF is resolved. Named
   *  agents match the marker against their own session transcript instead of
   *  the chat source-map lookup (#11164). */
  onCitation?: (citation: CitationDeepLink) => void;
  /** Closes only the FileViewer instance that owns this PDF iframe. */
  onEscape?: () => void;
  /** The host pane's Download, which saves the stored file. The ⋯ menu's
   *  Download and Ctrl/Cmd-S in the pages both run it; the reader never
   *  saves its own bytes, because they are the preview (a .docx or .pptx
   *  arrives as its PDF render). Without one, neither is offered. */
  onDownload?: () => void;
  /** The frame's `title`: fixed text for what the frame is ("Document"),
   *  never a file's name, which no attribute may carry (an error tracker's
   *  click breadcrumbs read `title`). */
  title?: string;
  /** The id of the element whose text names the frame (`aria-labelledby`):
   *  how a host gives it a file's name as its accessible name. */
  labelledBy?: string;
  /** Whether the frame takes focus when its document loads (the default).
   *  A host whose page answers its own keys (a review queue's shortcuts)
   *  keeps focus where the reader left it. */
  focusOnLoad?: boolean;
}

const PdfReader = ({
  fileContentResponse,
  fileUrl,
  onCitation,
  onEscape,
  onDownload,
  title,
  labelledBy,
  focusOnLoad = true,
}: PdfReaderProps) => {
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const removeEscapeListenerRef = useRef<(() => void) | null>(null);
  const removeFindListenerRef = useRef<(() => void) | null>(null);
  const readingAidsRef = useRef<ReadingAidsHandle>(null);
  const [iframeIsLoaded, setIframeIsLoaded] = useState(false);
  // The reading aids' view of the frame (#12481); null until the document's
  // pages have loaded, and reset with every document.
  const [viewerState, setViewerState] = useState<ViewerState | null>(null);
  const [outline, setOutline] = useState<OutlineEntry[]>([]);
  const [findState, setFindState] = useState<FindState | null>(null);
  const [outlineOpen, setOutlineOpen] = useState(false);
  // The row folds on its own frame, not the viewport: a document beside an
  // artifact in the overlay is half a drawer wide on a desktop screen.
  const { frameRef, frameWidth } = useFrameWidth<HTMLDivElement>();
  const compact =
    frameWidth !== null && frameWidth < READING_AIDS_COMPACT_BELOW_PX;

  // Use the pre-fetched file content instead of making a second request
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  // Create a blob URL from the pre-fetched content
  useEffect(() => {
    if (fileContentResponse) {
      // A reader that has gone (or moved to other bytes) before `blob()`
      // resolves makes no object URL: nothing would ever revoke it, and it
      // would hold the document in memory for the life of the page.
      let current = true;
      fileContentResponse.blob().then((blob: Blob) => {
        if (!current) return;
        setPdfBlobUrl(URL.createObjectURL(blob));
      });
      return () => {
        current = false;
      };
    } else if (fileUrl) {
      // Fallback to URL if no content response (for backward compatibility)
      setPdfBlobUrl(fileUrl);
    }
  }, [fileContentResponse, fileUrl]);

  useEffect(
    () => () => {
      removeEscapeListenerRef.current?.();
      removeFindListenerRef.current?.();
    },
    []
  );

  // Revoke each generated blob URL when it is replaced or unmounted.
  useEffect(() => {
    return () => {
      if (pdfBlobUrl && pdfBlobUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  // Read at message time: the frame's listener is attached once per load,
  // and the host's handler may change under it.
  const onDownloadRef = useRef(onDownload);
  useEffect(() => {
    onDownloadRef.current = onDownload;
  }, [onDownload]);

  // Setup handle to the iframe for message passing
  const onIframeMessage = useCallback((message: Msg) => {
    if ("requestDownload" in message) {
      onDownloadRef.current?.();
      return;
    }
    if ("state" in message) {
      setViewerState(message.state);
      return;
    }
    if ("outline" in message) {
      setOutline(message.outline);
      return;
    }
    if ("find" in message) {
      setFindState(message.find);
      return;
    }
  }, []);

  // Only initialize message passing when iframe is loaded
  const [sendMessage] = useIframeMessagePassing(
    iframeRef,
    onIframeMessage,
    iframeIsLoaded
  );

  // A citation clicked inside the rendered PDF opens in the document panel
  // rather than navigating the app away (#11164).
  useCitationLinkInterception(iframeRef, iframeIsLoaded, onCitation);

  useEffect(() => {
    if (pdfBlobUrl === null) {
      return;
    }
    setIframeIsLoaded(false);
    setViewerState(null);
    setOutline([]);
    setFindState(null);
    setOutlineOpen(false);
  }, [pdfBlobUrl]);

  // Post the highlight only when there is something to highlight: an empty
  // packet reads as "scroll to page 1" in the viewer, which is how the agents
  // outputs preview kept losing the user's scroll position (#11266).
  useEffect(() => {
    if (!iframeIsLoaded) return;
    const message = buildHighlightMessage(fileViewerData);
    if (message) sendMessage(message);
  }, [iframeIsLoaded, fileViewerData, sendMessage]);

  if (pdfBlobUrl === null) {
    return null;
  }

  const url = new URL(
    "/public/pdfjs-4.5.136-dist/web/viewer.html",
    import.meta.url
  );

  const pdfName = fileViewerData.fileName || "untitled-document.pdf";
  url.search = new URLSearchParams({
    file: pdfBlobUrl + "#" + encodeURIComponent(pdfName),
    authorization: "Bearer " + getSessionToken(),
  }).toString();

  const iframeSrc = url.href;
  const iframe = (
    <iframe
      ref={iframeRef}
      onLoad={() => {
        setIframeIsLoaded(true);
        const iframe = iframeRef.current;
        // The viewer's chrome follows the reader's OS scheme by default;
        // inside a light product a dark toolbar reads as another app
        // (#11426, keeper ruling D). pdf.js guards its dark styles with
        // html:not(.is-light), so this one class forces the light chrome.
        try {
          iframe?.contentDocument?.documentElement.classList.add("is-light");
        } catch {
          // Cross-origin in odd embeddings: leave the default chrome.
        }
        removeEscapeListenerRef.current?.();
        if (iframe) {
          const removeEscapeListener = listenForViewerIframeEscape(iframe, () =>
            onEscape?.()
          );
          iframe.dataset.escapeListenerReady = "true";
          removeEscapeListenerRef.current = () => {
            removeEscapeListener();
            delete iframe.dataset.escapeListenerReady;
          };
          // The find keys open the row's Find, not pdf.js's hidden bar.
          removeFindListenerRef.current?.();
          removeFindListenerRef.current = listenForViewerFindShortcut(
            iframe,
            (shortcut) =>
              readingAidsRef.current?.findShortcut(shortcut) ?? false
          );
          if (focusOnLoad) iframe.focus();
        }
      }}
      className="h-full w-full"
      title={title}
      aria-labelledby={labelledBy}
      src={iframeSrc}
      id="fp-pdf-viewer"
    />
  );
  const showOutline = outlineOpen && outline.length > 0;
  return (
    <div
      ref={frameRef}
      // The viewer's grey ground (fp-chrome.css --body-bg-color), so the
      // frame and the pages' ground are one colour while the PDF loads.
      className="flex h-full w-full min-w-0 flex-col bg-[#dedee0]"
      // The find keys from the row itself (its Find field included); the
      // frame's own keys never reach here, it takes them above.
      onKeyDown={(event) => {
        const shortcut = findShortcutOf(event);
        if (shortcut && readingAidsRef.current?.findShortcut(shortcut)) {
          event.preventDefault();
        }
      }}
    >
      {/* The reading aids on a quiet row above the pages (the design
          board's second row), never over them. */}
      <ReadingAids
        ref={readingAidsRef}
        state={viewerState}
        find={findState}
        hasOutline={outline.length > 0}
        outlineOpen={outlineOpen}
        onToggleOutline={() => setOutlineOpen((open) => !open)}
        send={(command) => {
          // A closed find has no matches to step through.
          if (command.type === "findClose") setFindState(null);
          sendMessage({ command });
        }}
        compact={compact}
        onDownload={onDownload}
      />
      <div className="relative flex min-h-0 w-full min-w-0 flex-1">
        {/* Beside the pages in a wide frame; over them, as a sheet, in a
            narrow one, where a 240px column would leave no page. */}
        {showOutline && (
          <div
            className={
              compact
                ? "absolute inset-y-0 left-0 z-10 [box-shadow:var(--fp-lift-overlay)]"
                : "relative h-full flex-none"
            }
          >
            <OutlineColumn
              outline={outline}
              currentPage={viewerState?.page ?? 0}
              onPick={(entry) => {
                sendMessage({
                  command: { type: "goToOutline", dest: entry.dest },
                });
                if (compact) setOutlineOpen(false);
              }}
            />
          </div>
        )}
        <div className="relative h-full min-w-0 flex-1">{iframe}</div>
      </div>
    </div>
  );
};

// Spelled as the union rather than Omit<Msg, ...>: keyof over a union of
// disjoint members intersects to never, so the Omit form collapses to {} and
// checks nothing at call sites.
type SendMessageCallback = (message: HighlightMessage | CommandMessage) => void;
type ReceiveMessageCallback = (message: Msg) => void;

/**
 * This hook allows us to encapsulate the `postMessage()` and `addEventListener("message")`
 * functionality of iframes into a more digestible manner.
 *
 * It gives us a primitive to send messages, and allows you to pass in a function to run
 * every time there's a message being received.
 *
 * The return value of this function is stable - it can be used like a setter of `useState`.
 */
const useIframeMessagePassing = (
  iframeRef: MutableRefObject<HTMLIFrameElement | null>,
  onMessage: ReceiveMessageCallback,
  isIframeLoaded: boolean
) => {
  // Setup ref to store the send message callback
  const sendMessageRef = useRef<SendMessageCallback>(() => {
    throw new Error("Used sendMessage too early!");
  });

  useEffect(() => {
    // Only initialize when iframe is loaded
    if (!isIframeLoaded) {
      return;
    }

    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow) {
      // Don't throw error, just return - iframe might not be ready yet
      return;
    }

    // Setup the `sendMessage` function
    sendMessageRef.current = (message) => {
      const msg = { ...message, IS_PDF_VIEWER_MESSAGE: "YES" };

      // Our own origin, which the viewer is served from: reading the frame's
      // `location` throws once it is on another origin's page, and a frame
      // there simply drops a message addressed to ours (#14962).
      iframeWindow.postMessage(msg, window.location.origin);
    };

    // Setup the `onMessage` event listener
    const receiveMessage = (event: MessageEvent) => {
      // Only this reader's own frame speaks for it. Another reader on the
      // page, or any other frame (an HTML output's), must not drive this
      // one: start its host's Download or move its state.
      if (event.source !== iframeWindow) return;
      if (
        event.data &&
        typeof event.data === "object" &&
        "IS_PDF_VIEWER_MESSAGE" in event.data
      ) {
        onMessage(event.data as Msg);
      }
    };

    window.addEventListener("message", receiveMessage);
    return () => window.removeEventListener("message", receiveMessage);
  }, [onMessage, isIframeLoaded]);

  // Memoized so its identity changes only when the iframe (re)loads — it sits
  // in the highlight effect's deps, and a per-render identity made that effect
  // re-post the highlight on every parent re-render (#11266).
  const sendMessage: SendMessageCallback = useCallback(
    (message) => {
      // Safety check - only send if iframe is loaded
      if (!isIframeLoaded || !iframeRef.current?.contentWindow) {
        console.warn("Cannot send message: iframe not ready");
        return;
      }
      sendMessageRef.current(message);
    },
    // iframeRef is a ref object — stable by construction, not a dependency.
    [isIframeLoaded]
  );
  return [sendMessage];
};

export default PdfReader;
